import { useEffect, useMemo, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Plus, ArrowLeft, FloppyDisk, Sparkle, ArrowUpRight, Receipt, ListChecks, Flag, Image as ImageIcon, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import YadidAI from "@/components/YadidAI";
import HealthTrend from "@/components/HealthTrend";
import DocumentsPanel from "@/components/DocumentsPanel";
import RelationshipTimeline from "@/components/RelationshipTimeline";
import NoteViewerDialog from "@/components/NoteViewerDialog";
import InvoiceQuickViewDialog from "@/components/InvoiceQuickViewDialog";
import TaskQuickViewDialog from "@/components/TaskQuickViewDialog";
import ClientHealthStoryline from "@/components/ClientHealthStoryline";
import ClientHealthBrief from "@/components/ClientHealthBrief";
import { DemoBadge } from "@/components/DemoBadge";
import ClientHealthDigestToggle from "@/components/ClientHealthDigestToggle";
import AttachmentBadge from "@/components/AttachmentBadge";
import ContactPicker from "@/components/ContactPicker";
import ContactsPanel from "@/components/ContactsPanel";
import ActivitiesTab from "@/components/ActivitiesTab";
import ActivityPill from "@/components/ActivityPill";
import { isMarkdownNote, previewText } from "@/lib/noteTypes";
import { CLIENT_TIERS, CLIENT_HEALTHS } from "@/lib/enumLabels";
import { ENGAGEMENT_TYPE_LABEL, ENGAGEMENT_STAGE_LABEL, ENGAGEMENT_STATUS_LABEL, ENGAGEMENT_HEALTH_LABEL } from "@/lib/enumLabels";

const EMBRACE_LABEL = {
  listen: "Listen",
  reveal: "Reveal",
  reimagine: "Reimagine",
  guide: "Guide",
};

// Iter 85 → 85.4 · All Contact-related constants (CONTACT_ROLES,
// CONTACT_POSTURES, CONTACT_INFLUENCES, ROLE_LABEL, POSTURE_LABEL,
// POSTURE_TONE, DECAY_DAYS, isDecayed, fmtRelative, EMPTY_CONTACT) now
// live in components/ContactsPanel.jsx. This file imports the panel
// only — the split prevents the class of missing-identifier bugs that
// shipped in 85.3.

const fmt$ = (n) => "$" + Number(n || 0).toLocaleString();

// ─────────────────────────────────────────────────────────────────────────
// ClientHeroImage — 96x96 square Embrace atmosphere image with an
// inline uploader. Falls back to a Yadid-texture initials square when
// no hero_image_url is set. On hover shows an overlay "Change" or
// "Add image" action. Uploads go through POST /clients/{id}/hero-image;
// clear via DELETE /clients/{id}/hero-image.
// ─────────────────────────────────────────────────────────────────────────
function ClientHeroImage({ client, onUpdated }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const monogram = (client.name || "")
    .split(/\s+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "").join("") || "—";

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/clients/${client.id}/hero-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUpdated(data.hero_image_url);
      toast.success("Atmosphere image updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };
  const clear = async () => {
    try {
      await api.delete(`/clients/${client.id}/hero-image`);
      onUpdated(null);
      toast.success("Atmosphere image cleared");
    } catch (e) {
      toast.error("Clear failed");
    }
  };

  const hasImage = !!client.hero_image_url;
  return (
    <div className="relative group shrink-0" data-testid="client-hero-image">
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={(e) => upload(e.target.files?.[0])}
        data-testid="client-hero-image-input"
      />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="block w-24 h-24 border border-border overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-brass/60 relative"
        aria-label={hasImage ? "Change atmosphere image" : "Add atmosphere image"}
        data-testid="client-hero-image-btn"
      >
        {hasImage ? (
          <img src={client.hero_image_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center"
            style={{
              background: "radial-gradient(circle at 30% 30%, rgba(176,126,69,0.28), rgba(176,126,69,0.10) 55%, rgba(176,126,69,0.04) 100%)",
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 900,
              fontSize: "2rem",
              letterSpacing: "-0.01em",
              color: "hsl(var(--brass))",
            }}
          >
            {monogram}
          </div>
        )}
        <div className="absolute inset-0 bg-primary/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-primary-foreground text-[0.5938rem] font-mono uppercase tracking-widest">
          {uploading ? "Uploading…" : hasImage ? "Change" : "Add image"}
        </div>
      </button>
      {hasImage && (
        <button
          type="button"
          onClick={clear}
          className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-card border border-border text-muted-foreground hover:text-destructive hover:border-destructive flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Clear atmosphere image"
          data-testid="client-hero-image-clear"
        >
          <Trash size={10} weight="bold" />
        </button>
      )}
    </div>
  );
}

/**
 * ClientDetail — the universal source of truth for a client relationship.
 *
 * Every panel BELOW the "Strategy / Health / Stakeholders" trio surfaces
 * records that already exist through engagements, invoices, tasks, and
 * notes. We never duplicate: each row is a direct link back to the
 * originating record (its engagement, its invoice modal, its task modal,
 * or its note viewer). The engagement remains the detailed source of
 * work — this page is the aggregated lens across all of them.
 */
export default function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "notes";
  const setActiveTab = (v) => {
    // Preserve any other query params; only rewrite the ?tab= slice so the
    // URL stays a shareable deep link into a specific tab.
    const next = new URLSearchParams(searchParams);
    next.set("tab", v);
    setSearchParams(next, { replace: true });
  };

  const [client, setClient] = useState(null);
  const [engs, setEngs] = useState([]);
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [tasks, setTasks] = useState([]);
  // {engagement_id: roadmap} — powers the Embrace tab summary. Keyed lookup
  // keeps the render trivial and lets us hydrate lazily as engagements load.
  const [roadmaps, setRoadmaps] = useState({});

  const [aiOpen, setAiOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // User list for the relationship-owner picker. Loaded once on mount —
  // small firm, few users; a full list is cheaper than a search endpoint.
  const [users, setUsers] = useState([]);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState({ title: "", body: "", note_type: "meeting", tags: "", contact_ids: [], engagement_id: "", activity_briefing_id: "" });

  const [selectedNote, setSelectedNote] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [focusCommitmentIdx, setFocusCommitmentIdx] = useState(null);

  // ── Deep-link handlers · Iter 68 + 69 ──
  // See EngagementDetail for the strip-after-consume pattern rationale.
  useEffect(() => {
    const tid = searchParams.get("openTask");
    const nid = searchParams.get("openNote");
    const cidx = searchParams.get("openCommitment");
    if (!tid && !nid) return;
    let consumed = false;
    if (tid && tasks && tasks.length) {
      const t = tasks.find((x) => x.id === tid);
      if (t) setSelectedTask(t);
      consumed = true;
    }
    if (nid && notes && notes.length) {
      const n = notes.find((x) => x.id === nid);
      if (n) {
        setSelectedNote(n);
        setFocusCommitmentIdx(Number.isFinite(parseInt(cidx, 10)) ? parseInt(cidx, 10) : null);
      }
      consumed = true;
    }
    if (consumed) {
      const next = new URLSearchParams(searchParams);
      next.delete("openTask");
      next.delete("openNote");
      next.delete("openCommitment");
      setSearchParams(next, { replace: true });
    }
  }, [tasks, notes, searchParams, setSearchParams]);

  const load = async () => {
    // Single rollup call replaces the per-endpoint fan-out. The backend
    // aggregates engagements + tasks + invoices + roadmaps + notes so the
    // page hydrates in one round trip even on very active client accounts.
    api.get(`/clients/${id}`).then((r) => setClient(r.data));
    try {
      const { data } = await api.get(`/clients/${id}/rollups`);
      setEngs(data.engagements || []);
      setNotes(data.notes || []);
      setInvoices(data.invoices || []);
      setTasks(data.tasks || []);
      setRoadmaps(data.roadmaps || {});
    } catch (e) {
      console.warn("Client rollups failed, falling back to per-endpoint loads", e);
      // Defensive fallback so the page still works if the aggregate endpoint
      // errors — reuses the individual endpoints the client used to fan out to.
      const { data: engList } = await api.get(`/engagements?client_id=${id}`);
      setEngs(engList);
      api.get(`/notes?client_id=${id}`).then((r) => setNotes(r.data));
      api.get(`/invoices?client_id=${id}`).then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
      if (engList.length) {
        const [taskLists, roadmapPairs] = await Promise.all([
          Promise.all(engList.map((e) => api.get(`/tasks?engagement_id=${e.id}`).then((r) => r.data).catch(() => []))),
          Promise.all(engList.map((e) => api.get(`/engagements/${e.id}/roadmap`).then((r) => [e.id, r.data]).catch(() => [e.id, null]))),
        ]);
        const flat = taskLists.flatMap((list, i) => list.map((t) => ({ ...t, engagement_name: engList[i].name })));
        setTasks(flat);
        setRoadmaps(Object.fromEntries(roadmapPairs));
      }
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  // Iter 85 · Users list for the relationship-owner picker. One shot on
  // mount; the list rarely changes during a session.
  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data || [])).catch(() => setUsers([]));
  }, []);

  const engById = useMemo(() => Object.fromEntries(engs.map((e) => [e.id, e])), [engs]);

  // Iter 85.41 · Precompute activity_id → title lookup once so the
  // Notes tab renders the brass Activity pill in O(1) per row.
  // Must sit ABOVE the `if (!client) return …` guard so hooks run in
  // the same order every render (rules-of-hooks / CRA prod build).
  const activityTitleByClient = useMemo(() => {
    const m = {};
    for (const n of notes || []) if (n.note_type === "activity_briefing") m[n.id] = n.title;
    return m;
  }, [notes]);

  if (!client) return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;

  const save = async (patch) => {
    const { data } = await api.put(`/clients/${id}`, patch);
    setClient(data);
    toast.success("Partner organization saved");
  };

  // ─── Contacts (Iter 85) ─────────────────────────────────────────────
  // Discrete endpoints so is_primary invariant is enforced server-side
  // and the UI never has to hand-roll it. Add/edit share one dialog —
  // owned by ContactsPanel (Iter 85.4 split).

  const addNote = async () => {
    if (!note.title || !note.body) return toast.error("Title and body required");
    await api.post("/notes", {
      ...note,
      client_id: id,
      // Iter 85.38 · propagate optional Engagement + Activity so meeting
      // notes captured from ClientDetail can roll up under a specific
      // activity when the partner picks one. Null values are safe —
      // backend validates scope and silently drops mismatches.
      engagement_id: note.engagement_id || null,
      activity_briefing_id: (note.note_type === "meeting" && note.activity_briefing_id) ? note.activity_briefing_id : null,
      tags: note.tags.split(",").map((s) => s.trim()).filter(Boolean),
      contact_ids: note.contact_ids || [],
    });
    setNote({ title: "", body: "", note_type: "meeting", tags: "", contact_ids: [], engagement_id: "", activity_briefing_id: "" });
    setNoteOpen(false);
    load();
    toast.success("Note added");
  };

  const activeEngs = engs.filter((e) => (e.status || "").toLowerCase() === "active");
  const openTasks = tasks.filter((t) => t.status !== "done");
  const invoiceTotal = invoices.reduce((s, i) => s + (i.total || 0), 0);
  const invoicePaid = invoices.filter((i) => i.status === "paid").reduce((s, i) => s + (i.total || 0), 0);

  const tabCls =
    "rounded-none data-[state=active]:border-b-2 data-[state=active]:border-primary " +
    "data-[state=active]:bg-transparent data-[state=active]:text-primary " +
    "text-xs uppercase font-mono tracking-widest px-4 py-3 whitespace-nowrap shrink-0";

  return (
    <PageShell size="default">
      {/* Iter 85.62 · Contextual back — uses history when available so
          Advisory Desk → Client → Back returns to Advisory Desk rather
          than the flat /clients list. Falls back to /clients only when
          the partner deep-linked in. */}
      <button
        type="button"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/clients"))}
        data-testid="client-back-btn"
        className="text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-primary flex items-center gap-1 mb-4"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <div className="flex items-start justify-between mb-6 gap-6">
        <div className="flex items-start gap-5 flex-1 min-w-0">
          {/* Hero image (Embrace atmosphere) — 96x96 square. Uploader
              opens on click; graceful fallback to Yadid-texture initials
              when unset. */}
          <ClientHeroImage client={client} onUpdated={(hero) => setClient({ ...client, hero_image_url: hero })} />
          <div className="min-w-0">
            <div className="text-[0.625rem] font-mono uppercase tracking-[0.2em] text-primary">{client.industry || "—"}</div>
            <div className="mt-2 flex items-center gap-3">
              <h1 className="font-display text-4xl font-bold tracking-tight">{client.name}</h1>
              <DemoBadge show={!!client.is_demo} className="text-[11px]" />
            </div>
            <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
              <span>{client.hq_location || "—"}</span>
              <span>·</span>
              <span>{client.company_size || "—"}</span>
              {client.website && <><span>·</span><a href={client.website.startsWith("http") ? client.website : `https://${client.website}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">{client.website}</a></>}
            </div>
          </div>
        </div>
        <Button onClick={() => setAiOpen(true)} className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 shrink-0" data-testid="ai-on-client">
          <Sparkle size={14} weight="fill" className="mr-2" /> Ask YadidAi about this client
        </Button>
      </div>

      {/* Quick controls */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Tier</div>
          <Select value={client.tier} onValueChange={(v) => save({ tier: v })}>
            <SelectTrigger data-testid="client-tier-select" className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-lg font-bold"><SelectValue /></SelectTrigger>
            <SelectContent>{CLIENT_TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Health</div>
          <Select value={client.relationship_health} onValueChange={(v) => save({ relationship_health: v })}>
            <SelectTrigger data-testid="client-health-select" className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-lg font-bold"><SelectValue /></SelectTrigger>
            <SelectContent>{CLIENT_HEALTHS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Owner</div>
          <Select
            value={client.relationship_owner_user_id || "__unassigned"}
            onValueChange={(v) => save({ relationship_owner_user_id: v === "__unassigned" ? null : v })}
          >
            <SelectTrigger data-testid="client-owner-select" className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-lg font-bold">
              <SelectValue placeholder={client.relationship_owner || "Unassigned"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__unassigned">Unassigned</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id} data-testid={`owner-option-${u.id}`}>{u.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="border border-border bg-card p-4">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Ann. revenue</div>
          <Input type="number" defaultValue={client.annual_revenue || ""} onBlur={(e) => save({ annual_revenue: parseFloat(e.target.value) || null })} className="rounded-none bg-transparent border-0 p-0 h-auto font-display text-lg font-bold" />
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Iter 85.33 · Notes moved to position #1 so the immediate working
            intelligence (meeting notes, action items, follow-ups, decisions,
            open questions) leads the tab row. All other tabs preserve their
            relative order. Deep links via ?tab= continue to resolve. */}
        <TabsList className="rounded-none bg-transparent border-b border-border w-full justify-start p-0 h-auto overflow-x-auto">
          {/* Iter 85.62 · Same strategic tab hierarchy as EngagementDetail,
              adapted for Client canon. Client has no Deliverables (belong
              to Engagements per spec) and no Milestones surface — slots
              skipped. Existing `value=` attributes preserved so deep
              links via ?tab= keep resolving. */}
          <TabsTrigger value="embrace" className={tabCls} data-testid="tab-client-embrace">Embrace ({activeEngs.length})</TabsTrigger>
          <TabsTrigger value="activities" className={tabCls} data-testid="tab-client-activities">Activities</TabsTrigger>
          <TabsTrigger value="engagements" className={tabCls} data-testid="tab-client-engagements">Engagements ({engs.length})</TabsTrigger>
          <TabsTrigger value="tasks" className={tabCls} data-testid="tab-client-tasks">Tasks ({openTasks.length})</TabsTrigger>
          <TabsTrigger value="notes" className={tabCls} data-testid="tab-client-notes">Notes ({notes.length})</TabsTrigger>
          <TabsTrigger value="documents" className={tabCls} data-testid="tab-client-documents">Documents</TabsTrigger>
          <TabsTrigger value="stakeholders" className={tabCls} data-testid="tab-client-contacts">Contacts ({(client.stakeholders||[]).length})</TabsTrigger>
          <TabsTrigger value="strategy" className={tabCls} data-testid="tab-client-strategy">Strategy</TabsTrigger>
          <TabsTrigger value="storyline" className={tabCls} data-testid="tab-client-storyline">Storyline</TabsTrigger>
          <TabsTrigger value="timeline" className={tabCls} data-testid="tab-client-timeline">Timeline</TabsTrigger>
          <TabsTrigger value="invoices" className={tabCls} data-testid="tab-client-invoices">Invoices ({invoices.length})</TabsTrigger>
          <TabsTrigger value="brief" className={tabCls} data-testid="tab-client-brief">Partner health</TabsTrigger>
          <TabsTrigger value="health" className={tabCls} data-testid="tab-health-trend">Health trend</TabsTrigger>
        </TabsList>

        <TabsContent value="strategy" className="mt-6 space-y-6">
          <div className="border border-border bg-card p-6">
            <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">Strategic context</div>
            <Textarea defaultValue={client.strategic_context || ""} onBlur={(e) => save({ strategic_context: e.target.value })} rows={5} placeholder="Why they matter, competitive dynamics, situation…" className="rounded-none bg-secondary/40" data-testid="client-strategic-context" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="border border-border bg-card p-6">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Known priorities</div>
              <Textarea defaultValue={(client.known_priorities || []).join("\n")} onBlur={(e) => save({ known_priorities: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={5} placeholder="One per line" className="rounded-none bg-secondary/40" />
            </div>
            <div className="border border-border bg-card p-6">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-3">Pain points</div>
              <Textarea defaultValue={(client.known_pain_points || []).join("\n")} onBlur={(e) => save({ known_pain_points: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} rows={5} placeholder="One per line" className="rounded-none bg-secondary/40" />
            </div>
          </div>
        </TabsContent>

        {/*
          Iter 85 · Contacts — the CRM roster tab. Extracted into
          ContactsPanel.jsx in Iter 85.4 to keep ClientDetail lean and
          prevent the class of missing-identifier bugs that shipped in
          85.3. All Contact state, handlers, and JSX (add/edit dialog,
          primary toggle, last-contacted chip, decay nudge, history
          drawer, CSV import) live inside the panel now.
        */}
        <TabsContent value="stakeholders" className="mt-0">
          <ContactsPanel client={client} onClientUpdated={setClient} users={users} />
        </TabsContent>

        {/* Iter 85.34 · Activities tab — dedicated view of Activity
            Briefings across every engagement in this client. Backed by
            the canonical notes collection filtered on
            note_type=activity_briefing. */}
        <TabsContent value="activities" className="mt-6">
          <ActivitiesTab scope="client" clientId={id} engagements={engs} />
        </TabsContent>

        <TabsContent value="engagements" className="mt-6">
          <div className="border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="text-left px-5 py-3 font-normal">Engagement</th>
                  <th className="text-left px-3 py-3 font-normal">Type</th>
                  <th className="text-left px-3 py-3 font-normal">Stage</th>
                  <th className="text-left px-3 py-3 font-normal">Status</th>
                  <th className="text-left px-3 py-3 font-normal">Health</th>
                </tr>
              </thead>
              <tbody>
                {engs.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-muted-foreground text-xs">No engagements yet.</td></tr>}
                {engs.map((e) => (
                  <tr key={e.id} className="border-b border-border/50 hover:bg-secondary/30">
                    <td className="px-5 py-3"><Link to={`/engagements/${e.id}`} className="font-medium hover:text-primary" data-testid={`client-engagement-${e.id}`}>{e.name}</Link></td>
                    <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest">{ENGAGEMENT_TYPE_LABEL[e.engagement_type] || e.engagement_type}</td>
                    <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest">{ENGAGEMENT_STAGE_LABEL[e.stage] || e.stage}</td>
                    <td className="px-3 py-3 text-xs uppercase font-mono tracking-widest">{ENGAGEMENT_STATUS_LABEL[e.status] || e.status}</td>
                    <td className="px-3 py-3"><span className={`dot dot-${e.health}`} title={ENGAGEMENT_HEALTH_LABEL[e.health] || e.health} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/*
          Embrace summary — one row per active engagement showing its approved
          primary emphasis (Listen / Reveal / Reimagine / Guide). Clicking a
          row jumps to that engagement's Embrace tab, which is the source of
          truth for method work; this client-level view is a lens only, not a
          separate store of phase state.
        */}
        <TabsContent value="embrace" className="mt-6">
          <div className="border border-border bg-card">
            <div className="border-b border-border px-5 py-3">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary">Active method emphasis · {activeEngs.length}</div>
              <p className="text-xs text-muted-foreground mt-1">
                Primary movement in play across every active engagement for this client. Open an engagement to work in its Embrace workspace.
              </p>
            </div>
            {activeEngs.length === 0 && (
              <div className="px-5 py-10 text-center text-xs text-muted-foreground">No active engagements yet — the Embrace lens will populate as work begins.</div>
            )}
            {activeEngs.map((e) => {
              const rm = roadmaps[e.id];
              const phase = rm?.current_phase;
              const phaseLabel = phase ? (EMBRACE_LABEL[phase] || phase) : "—";
              const reflections = (rm?.phases || []).reduce((s, p) => s + (p.reflections?.length || 0), 0);
              return (
                <Link
                  key={e.id}
                  to={`/engagements/${e.id}?tab=embrace`}
                  data-testid={`client-embrace-row-${e.id}`}
                  className="block border-b border-border/60 last:border-b-0 px-5 py-4 hover:bg-secondary/40 focus-visible:outline-none focus-visible:bg-secondary/40 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">{ENGAGEMENT_TYPE_LABEL[e.engagement_type] || e.engagement_type} · {ENGAGEMENT_STAGE_LABEL[e.stage] || e.stage}</div>
                      <div className="font-display font-bold text-base mt-0.5 truncate">{e.name}</div>
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-1">
                        {reflections > 0 ? `${reflections} reflection${reflections === 1 ? "" : "s"} on file` : "No reflections yet"}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">Primary emphasis</div>
                      <div className={`font-display font-bold text-lg tracking-tight ${phase ? "text-primary" : "text-muted-foreground"}`}>{phaseLabel}</div>
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary/80 mt-1 inline-flex items-center gap-1">
                        Open Embrace <ArrowUpRight size={11} weight="bold" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </TabsContent>

        {/*
          Invoices roll-up — every invoice against every engagement of this
          client. Clicking a row opens the invoice quick-view (same modal the
          Invoices workspace and EngagementInvoicesTab use), so the record is
          reached without ever leaving the client page. The engagement label
          is its own link straight into that engagement's Invoices tab.
        */}
        <TabsContent value="invoices" className="mt-6">
          <div className="border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-5 py-3">
              <div>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5"><Receipt size={11} weight="fill" /> Billing across all engagements</div>
                <div className="text-xs text-muted-foreground mt-1">{invoices.length} invoice{invoices.length === 1 ? "" : "s"} · {fmt$(invoicePaid)} paid of {fmt$(invoiceTotal)}</div>
              </div>
              <Link to="/invoices" className="text-[0.625rem] font-mono uppercase tracking-widest text-primary hover:opacity-80 inline-flex items-center gap-1">
                Open invoices workspace <ArrowUpRight size={11} weight="bold" />
              </Link>
            </div>
            {invoices.length === 0 ? (
              <div className="px-5 py-10 text-center text-xs text-muted-foreground">No invoices for this client yet.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-3 font-normal">Invoice</th>
                    <th className="text-left px-3 py-3 font-normal">Engagement</th>
                    <th className="text-left px-3 py-3 font-normal">Issued</th>
                    <th className="text-left px-3 py-3 font-normal">Status</th>
                    <th className="text-right px-5 py-3 font-normal">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const eng = engById[inv.engagement_id];
                    const tone = inv.status === "paid" ? "text-emerald-500"
                      : inv.status === "sent" ? "text-primary"
                      : inv.status === "overdue" ? "text-destructive"
                      : "text-muted-foreground";
                    return (
                      <tr
                        key={inv.id}
                        className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer"
                        onClick={() => setSelectedInvoice(inv)}
                        data-testid={`client-invoice-row-${inv.id}`}
                      >
                        <td className="px-5 py-3 font-medium">{inv.invoice_number}</td>
                        <td className="px-3 py-3 text-xs" onClick={(ev) => ev.stopPropagation()}>
                          {eng ? (
                            <Link to={`/engagements/${eng.id}?tab=invoices`} className="hover:text-primary" data-testid={`client-invoice-eng-${inv.id}`}>{eng.name}</Link>
                          ) : "—"}
                        </td>
                        <td className="px-3 py-3 text-xs font-mono text-muted-foreground">{inv.issue_date || "—"}</td>
                        <td className={`px-3 py-3 text-[0.625rem] font-mono uppercase tracking-widest ${tone}`}>{inv.status || "draft"}</td>
                        <td className="px-5 py-3 text-right font-medium tabular-nums">{fmt$(inv.total || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/*
          Tasks roll-up — every open task on every engagement, plus a small
          "done" summary. Clicking the title opens the task quick-view modal
          (same one Engagement Tasks tab uses); the engagement label routes
          directly to that engagement's Tasks tab.
        */}
        <TabsContent value="tasks" className="mt-6">
          <div className="border border-border bg-card">
            <div className="border-b border-border px-5 py-3">
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-1.5"><ListChecks size={11} weight="fill" /> Open tasks across all engagements</div>
              <div className="text-xs text-muted-foreground mt-1">{openTasks.length} open · {tasks.length - openTasks.length} done</div>
            </div>
            {openTasks.length === 0 ? (
              <div className="px-5 py-10 text-center text-xs text-muted-foreground">No open tasks — clear.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                  <tr className="border-b border-border">
                    <th className="text-left px-5 py-3 font-normal">Task</th>
                    <th className="text-left px-3 py-3 font-normal">Engagement</th>
                    <th className="text-left px-3 py-3 font-normal">Priority</th>
                    <th className="text-left px-3 py-3 font-normal">Status</th>
                    <th className="text-left px-3 py-3 font-normal">Due</th>
                  </tr>
                </thead>
                <tbody>
                  {openTasks.map((t) => {
                    const eng = engById[t.engagement_id];
                    return (
                      <tr
                        key={t.id}
                        className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer"
                        onClick={() => setSelectedTask(t)}
                        data-testid={`client-task-row-${t.id}`}
                      >
                        <td className="px-5 py-3 font-medium">{t.title}</td>
                        <td className="px-3 py-3 text-xs" onClick={(ev) => ev.stopPropagation()}>
                          {eng ? (
                            <Link to={`/engagements/${eng.id}?tab=tasks`} className="hover:text-primary" data-testid={`client-task-eng-${t.id}`}>{eng.name}</Link>
                          ) : (t.engagement_name || "—")}
                        </td>
                        <td className="px-3 py-3 text-[0.625rem] font-mono uppercase tracking-widest">{t.priority || "—"}</td>
                        <td className="px-3 py-3 text-[0.625rem] font-mono uppercase tracking-widest">{t.status || "—"}</td>
                        <td className="px-3 py-3 text-xs font-mono text-muted-foreground">{t.due_date || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </TabsContent>

        {/*
          Notes + memos roll-up. AI-authored memo bodies (diagnostic /
          strategic / insight) are typeset in the NoteViewerDialog, so the
          card here just shows a stripped preview. Every card opens the
          viewer — no duplicate copy of the note lives on the client page.
        */}
        <TabsContent value="notes" className="mt-6">
          <div className="flex justify-end mb-3">
            <Dialog open={noteOpen} onOpenChange={setNoteOpen}>
              <DialogTrigger asChild><Button size="sm" className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90" data-testid="add-client-note-btn"><Plus size={12} className="mr-1" /> New note</Button></DialogTrigger>
              <DialogContent className="rounded-none">
                <DialogHeader>
                  <DialogTitle>New note</DialogTitle>
                  <DialogDescription className="text-xs text-muted-foreground">
                    Capture a meeting, insight, or memo against this client. Tag contacts to bump their last-contacted timestamp automatically.
                  </DialogDescription>
                </DialogHeader>
                <Input placeholder="Title" value={note.title} onChange={(e) => setNote({ ...note, title: e.target.value })} className="rounded-none bg-secondary/40" />
                <Select value={note.note_type} onValueChange={(v) => setNote({ ...note, note_type: v, activity_briefing_id: v === "meeting" ? note.activity_briefing_id : "" })}>
                  <SelectTrigger className="rounded-none bg-secondary/40"><SelectValue /></SelectTrigger>
                  <SelectContent>{["meeting", "insight", "strategic_memo", "call_log"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                {/*
                  Iter 85.38 · Optional Engagement selector so a Meeting
                  Note captured from a Client page can roll up under a
                  specific engagement — a prerequisite for the Activity
                  picker below which is scoped to a Client × Engagement.
                  Uses the client's existing engagement rollup so no
                  extra fetch fires.
                */}
                <Select
                  value={note.engagement_id || "none"}
                  onValueChange={(v) => setNote({ ...note, engagement_id: v === "none" ? "" : v, activity_briefing_id: "" })}
                >
                  <SelectTrigger className="rounded-none bg-secondary/40" data-testid="client-note-engagement-select"><SelectValue placeholder="Engagement (optional)" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No engagement — partner-org-scoped</SelectItem>
                    {engs.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {/*
                  Iter 85.38 · Optional Activity picker — renders only
                  when the current note is a meeting note AND the
                  partner has selected an Engagement. Scoped to
                  activity briefings under this (client, engagement)
                  so a planning call / prep session / staff coord /
                  debrief / follow-up can be linked to its parent
                  activity without leaving the Partner Organization's Notes tab.
                */}
                {note.note_type === "meeting" && note.engagement_id && (() => {
                  const scopedActivities = (notes || []).filter((nn) =>
                    nn.note_type === "activity_briefing"
                    && nn.client_id === id
                    && nn.engagement_id === note.engagement_id
                  );
                  if (scopedActivities.length === 0) return null;
                  return (
                    <div data-testid="client-note-activity-picker">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                        Activity <span className="text-muted-foreground/60 normal-case tracking-normal">(optional — link to a client activity)</span>
                      </div>
                      <Select
                        value={note.activity_briefing_id || "none"}
                        onValueChange={(v) => setNote({ ...note, activity_briefing_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger className="rounded-none bg-secondary/40" data-testid="client-note-activity-select"><SelectValue placeholder="No activity" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No activity — standalone meeting note</SelectItem>
                          {scopedActivities.map((a) => {
                            const when = a.activity_date ? ` · ${a.activity_date}` : "";
                            return <SelectItem key={a.id} value={a.id}>{a.title}{when}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })()}
                <Input placeholder="Tags (comma-separated)" value={note.tags} onChange={(e) => setNote({ ...note, tags: e.target.value })} className="rounded-none bg-secondary/40" />
                {/* Iter 85.2 · Phase 3 — tag contacts referenced in this note. Server auto-bumps last_contacted_at on save. */}
                <div className="pt-1">
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1.5">Contacts on this note</div>
                  <ContactPicker
                    contacts={client.stakeholders || []}
                    value={note.contact_ids || []}
                    onChange={(ids) => setNote({ ...note, contact_ids: ids })}
                    testidPrefix="new-note-contact-picker"
                  />
                </div>
                <Textarea placeholder="Body…" rows={6} value={note.body} onChange={(e) => setNote({ ...note, body: e.target.value })} className="rounded-none bg-secondary/40" />
                <DialogFooter><Button onClick={addNote} className="rounded-none bg-primary text-primary-foreground" data-testid="add-client-note-save">Save</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-3">
            {notes.length === 0 && <div className="border border-border bg-card p-8 text-center text-muted-foreground text-xs">No notes captured yet.</div>}
            {notes.map((n) => {
              const eng = engById[n.engagement_id];
              const memo = isMarkdownNote(n);
              return (
                <button
                  key={n.id}
                  onClick={() => setSelectedNote(n)}
                  data-testid={`client-note-row-${n.id}`}
                  className="w-full text-left border border-border bg-card p-5 hover:border-primary/60 hover:bg-secondary/40 focus-visible:outline-none focus-visible:border-primary/60 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary flex items-center gap-2 flex-wrap">
                        <span>{n.note_type}</span>
                        <AttachmentBadge note={n} />
                        <ActivityPill
                          activityId={n.activity_briefing_id}
                          activityTitleBy={activityTitleByClient}
                          testidSuffix={n.id}
                        />
                      </div>
                      <div className="font-display font-bold text-base mt-0.5">{n.title}</div>
                      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                        {memo ? previewText(n.body) : n.body}
                      </p>
                      {eng && (
                        <div className="mt-2 text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                          via engagement · <span className="text-primary">{eng.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-right text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground shrink-0">
                      {new Date(n.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {n.tags?.length > 0 && <div className="mt-3 flex gap-1.5 flex-wrap">{n.tags.map((t) => <span key={t} className="text-[0.625rem] font-mono uppercase tracking-widest border border-border px-2 py-0.5 text-muted-foreground">{t}</span>)}</div>}
                </button>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="brief" className="mt-6">
          <ClientHealthBrief clientId={id} />
        </TabsContent>

        <TabsContent value="health" className="mt-6">
          <HealthTrend clientId={id} />
        </TabsContent>

        <TabsContent value="storyline" className="mt-6">
          <ClientHealthStoryline clientId={id} />
          <ClientHealthDigestToggle clientId={id} clientName={client?.name || "this partner organization"} />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          <DocumentsPanel clientId={id} />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <RelationshipTimeline clientId={id} />
        </TabsContent>
      </Tabs>

      <YadidAI open={aiOpen} onClose={() => setAiOpen(false)} contextClientId={id} />
      <NoteViewerDialog
        note={selectedNote}
        open={!!selectedNote}
        onOpenChange={(v) => { if (!v) { setSelectedNote(null); setFocusCommitmentIdx(null); } }}
        focusCommitmentIdx={focusCommitmentIdx}
      />
      <InvoiceQuickViewDialog invoice={selectedInvoice} open={!!selectedInvoice} onOpenChange={(v) => !v && setSelectedInvoice(null)} />
      <TaskQuickViewDialog
        task={selectedTask}
        open={!!selectedTask}
        onOpenChange={(v) => !v && setSelectedTask(null)}
        onTaskChange={(updated) => {
          if (!updated) return;
          setTasks((prev) => prev.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)));
          setSelectedTask((cur) => (cur && cur.id === updated.id ? { ...cur, ...updated } : cur));
        }}
      />
    </PageShell>
  );
}
